import {
  Autocomplete,
  InputAdornment,
  Paper,
  TextField,
  Typography,
} from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import FlagCircleOutlinedIcon from "@mui/icons-material/FlagCircleOutlined";
import { Box, Stack } from "@mui/system";
import { DataGridPro, GridColDef } from "@mui/x-data-grid-pro";
import dayjs from "dayjs";
import { useState } from "react";
import { useSearchParams } from "react-router-dom";

interface Props {
  tableHeadings: { [key: string]: string };
  kewordPerformance?: any;
  getDefaultDates?: any;
  handleTimeframeChange?: (event: any, value: any) => void;
  selectedTimeframe?: string;
  dateRange?: {
    startDate: string;
    endDate: string;
  };
}

const formatCurrency = (value: number | null | undefined | "N/A") => {
  if (value === null || value === undefined || value === "N/A") return value;
  return `${value.toLocaleString("en-US", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}$`;
};

const formatPercentage = (value: number | null | undefined | "N/A") => {
  if (value === null || value === undefined || value === "N/A") return value;
  return `${Math.round(value * 100)}%`;
};

const NoRowsOverlay = () => {
  return (
    <Stack height="100%" alignItems="center" justifyContent="center">
      <Typography>No data to Display</Typography>
    </Stack>
  );
};

const InsightKeyWordPerformance = ({
  tableHeadings,
  kewordPerformance,
  handleTimeframeChange,
  selectedTimeframe,
  dateRange,
}: Props) => {
  const [selectedKeyword, setSelectedKeyword] = useState<string>("All");
  const [searchParams] = useSearchParams();
  const insightType = searchParams.get("insightType");
  const timeFrames = [
    { label: "Insight timeframe", value: "insightTimeframe" },
    { label: "Last 30 Days", value: "last30" },
    { label: "Yesterday", value: "yesterday" },
    { label: "Last Week", value: "lastWeek" },
    { label: "Month to Date", value: "mtd" },
    { label: "Last Month", value: "lastMonth" },
    { label: "Year to Date", value: "ytd" },
    { label: "Since Bid Change", value: "bidChange" },
  ];

  // Extract unique keywords from the data
  const Keywords = [
    { label: "All" },
    ...Array.from(
      new Set(
        kewordPerformance?.query_output?.map((item: any) => item.kw_name) || []
      )
    ).map((kw) => ({ label: kw })),
  ];

  // --- HIGHLIGHT CALCULATION (before filtering) ---
  const allRows = kewordPerformance?.query_output || [];

  // Highlight styles
  const redStyle = {
    bgcolor: "rgba(253, 237, 237, 1)",
    border: "1px solid rgba(211, 47, 47, 0.5)",
  };
  const greenStyle = {
    bgcolor: "rgba(237, 247, 237, 1)",
    border: "1px solid rgba(46, 125, 50, 0.5)",
  };

  // Helper to get sorted ids for top/last N
  const getSortedIds = (
    field: string,
    top: number,
    last: number,
    isAsc = false
  ) => {
    const arr = allRows
      .map((item: any, idx: number) => ({
        id: item.kw_id ?? `row-${idx}`,
        value: item[field],
      }))
      .filter(
        (x: { id: string; value: any }) =>
          typeof x.value === "number" && !isNaN(x.value)
      );
    arr.sort((a: { id: string; value: any }, b: { id: string; value: any }) =>
      isAsc ? a.value - b.value : b.value - a.value
    );
    const topArr = arr
      .slice(0, top)
      .map((x: { id: string; value: any }) => x.id);
    const lastArr = arr
      .slice(-last)
      .map((x: { id: string; value: any }) => x.id);
    return {
      top: topArr,
      last: lastArr,
    };
  };
  // Helper for threshold-based highlights
  const getIdsByThreshold = (field: string, fn: (v: any) => boolean) => {
    return allRows
      .map((item: any, idx: number) => ({
        id: item.kw_id ?? `row-${idx}`,
        value: item[field],
      }))
      .filter((x: { id: string; value: any }) => fn(x.value))
      .map((x: { id: string; value: any }) => x.id);
  };

  // Spend: Red top 3, Green last 3
  const spendHL = getSortedIds("spend", 3, 3);
  // CAC: Red top 3
  const cacHL = getSortedIds("CAC", 3, 0);
  // FTD: Red = 0
  const ftdRed = getIdsByThreshold("ftd", (v) => v === 0);
  // QFTD: Red = 0
  const qftdRed = getIdsByThreshold("qftd", (v) => v === 0);
  // IMP Share: Red < 0.5, Green > 0.85
  const impShareRed = getIdsByThreshold(
    "imp_share",
    (v) => typeof v === "number" && v < 0.5
  );
  const impShareGreen = getIdsByThreshold(
    "imp_share",
    (v) => typeof v === "number" && v > 0.85
  );
  // IMP Share Top: Red < 0.5, Green > 0.85
  const impShareTopRed = getIdsByThreshold(
    "imp_top",
    (v) => typeof v === "number" && v < 0.5
  );
  const impShareTopGreen = getIdsByThreshold(
    "imp_top",
    (v) => typeof v === "number" && v > 0.85
  );
  // IMP Share Abs Top: Red < 0.5, Green > 0.85
  const impShareAbsTopRed = getIdsByThreshold(
    "imp_abs_top",
    (v) => typeof v === "number" && v < 0.5
  );
  const impShareAbsTopGreen = getIdsByThreshold(
    "imp_abs_top",
    (v) => typeof v === "number" && v > 0.85
  );
  // Average QS: Red < 7
  const avgQSRed = getIdsByThreshold(
    "avg_qs",
    (v) => typeof v === "number" && v < 7
  );
  // CPC: Red top 3, Green last 3
  const cpcHL = getSortedIds("cpc", 3, 3);
  // CVR: Red top 3, Green last 3
  const cvrHL = getSortedIds("CVR", 3, 3);
  // Profit: Red = 3 most negative, Green = 3 most positive
  // Helper for profit highlights
  const getProfitHighlightIds = () => {
    // Only consider rows with numeric profit
    const arr = allRows
      .map((item: any, idx: number) => ({
        id: item.kw_id ?? `row-${idx}`,
        value: item.Profit,
      }))
      .filter(
        (x: { id: string; value: any }) =>
          typeof x.value === "number" && !isNaN(x.value)
      ); // Sort ascending for negative, descending for positive

    const sortedAsc = [...arr].sort((a, b) => a.value - b.value); // most negative first
    const sortedDesc = [...arr].sort((a, b) => b.value - a.value); // most positive first
    const redArr = sortedAsc.slice(0, 3).map((x) => x.id); // 3 most negative
    const greenArr = sortedDesc.slice(0, 3).map((x) => x.id); // 3 most positive
    return { red: redArr, green: greenArr };
  };
  const profitHL = getProfitHighlightIds();
  // PV: Red top 3, Green last 3
  const getPVHighlightIds = () => {
    const arr = allRows
      .map((item: any, idx: number) => ({
        id: item.kw_id ?? `row-${idx}`,
        value: item.PV,
      }))
      .filter(
        (x: { id: string; value: any }) =>
          typeof x.value === "number" && !isNaN(x.value)
      );
    const sortedAsc = [...arr].sort((a, b) => a.value - b.value); // most negative first
    const sortedDesc = [...arr].sort((a, b) => b.value - a.value); // most positive first
    const redArr = sortedAsc.slice(0, 3).map((x) => x.id); // 3 most negative
    const greenArr = sortedDesc.slice(0, 3).map((x) => x.id); // 3 most positive
    return { red: redArr, green: greenArr };
  };
  const pvHL = getPVHighlightIds();
  // NRPF: Red top 3, Green last 3
  const getNRPFHighlightIds = () => {
    const arr = allRows
      .map((item: any, idx: number) => ({
        id: item.kw_id ?? `row-${idx}`,
        value: item.NRPF,
      }))
      .filter(
        (x: { id: string; value: any }) =>
          typeof x.value === "number" && !isNaN(x.value)
      ); // Sort ascending for negative, descending for positive

    const sortedAsc = [...arr].sort((a, b) => a.value - b.value); // most negative first
    const sortedDesc = [...arr].sort((a, b) => b.value - a.value); // most positive first
    const redArr = sortedAsc.slice(0, 3).map((x) => x.id); // 3 most negative
    const greenArr = sortedDesc.slice(0, 3).map((x) => x.id); // 3 most positive
    return { red: redArr, green: greenArr };
  };
  const nrpfHL = getNRPFHighlightIds();
  // --- END HIGHLIGHT CALCULATION ---

  // Filter performance data based on selected keyword
  const filteredPerformanceData = (
    kewordPerformance?.query_output || []
  ).filter((item: any) =>
    selectedKeyword === "All" ? true : item.kw_name === selectedKeyword
  );

  const performanceData = filteredPerformanceData.map((item: any) => ({
    ...item,
    spend: item?.spend ?? "N/A",
    CAC: item?.CAC ?? "N/A",
    ftd: item?.ftd ?? "N/A",
    qftd: item?.qftd ?? "N/A",
    imp_share: item?.imp_share ?? "N/A",
    imp_top: item?.imp_top ?? "N/A",
    imp_abs_top: item?.imp_abs_top ?? "N/A",
    avg_qs: item?.avg_qs ?? "N/A",
    cpc: item?.cpc ?? "N/A",
    CVR: item?.CVR ?? "N/A",
    Profit: item?.Profit ?? "N/A",
    PV: item?.PV ?? "N/A",
    NRPF: item?.NRPF ?? "N/A",
    Clickin: item?.Clickin ?? "N/A",
    Clickouts: item?.Clickouts ?? "N/A",
    cpc_diff_30_60: item?.cpc_diff_30_60 ?? "N/A",
    cvr_diff_30_60: item?.cvr_diff_30_60 ?? "N/A",
    imp_diff_30_60: item?.imp_diff_30_60 ?? "N/A",
    qs_diff_30_60: item?.qs_diff_30_60 ?? "N/A",
  }));

  const processedRows = performanceData.map((item: any, index: number) => ({
    id: item?.kw_id || `row-${index}`,
    keyword: item?.kw_name || "",
    match_type: item?.matchtype || "",
    spend: item?.spend,
    spendDisplay: formatCurrency(item?.spend),
    cac: formatCurrency(item?.CAC),
    ftd: item?.ftd,
    qftd: item?.qftd,
    imp_share: item?.imp_share,
    imp_share_top: item?.imp_top,
    imp_share_abs_top: item?.imp_abs_top,
    avg_qs:
      typeof item.avg_qs === "number" ? item.avg_qs.toFixed(2) : item.avg_qs,
    cpc: item?.cpc,
    cvr: item?.CVR,
    profit: formatCurrency(item?.Profit),
    pv: formatCurrency(item?.PV),
    nrpf: formatCurrency(item?.NRPF),
    clickin: item?.Clickin,
    clickouts: item?.Clickouts,
    Cpc_up: item?.cpc_diff_30_60,
    cvr_down: item?.cvr_diff_30_60,
    imp_share_down: item?.imp_diff_30_60,
    qs_down: item?.qs_diff_30_60,
    exact_traffic_mix: false,
  }));

  // Custom sort comparator for FTD and QFTD
  const ftdQftdSortComparator = (v1: any, v2: any) => {
    // Helper to assign sort weight
    const getWeight = (v: any) => {
      if (v === "N/A" || v === null || v === undefined) return -2;
      if (v === 0) return -1;
      return Number(v);
    };
    const w1 = getWeight(v1);
    const w2 = getWeight(v2);
    return w1 - w2;
  };

  // --- COLUMN DEFINITIONS WITH HIGHLIGHTS ---
  const columns: GridColDef[] = Object.entries(tableHeadings).map(
    ([field, headerName]) => {
      const baseColumn: GridColDef = {
        field,
        headerName,
        renderCell: (params: any) => (
          <Box
            sx={{
              padding: "3px",
              borderRadius: 1,
              display: "flex",
              alignItems: "center",
              justifyContent: "left",
              marginTop: "4px",
              height: "26px",
              width: "fit-content",
            }}
          >
            {params.value}
          </Box>
        ),
      };

      // --- Spend ---
      if (field === "spend") {
        return {
          ...baseColumn,
          sortingOrder: ["desc", "asc"] as const,
          renderCell: (params: any) => {
            const id = params.row.id;
            let style = {};
            if (spendHL.top.includes(id)) style = redStyle;
            else if (spendHL.last.includes(id)) style = greenStyle;
            return (
              <Box
                sx={{
                  padding: "3px",
                  borderRadius: 1,
                  ...style,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  marginTop: "4px",
                  height: "26px",
                  width: "fit-content",
                }}
              >
                {formatCurrency(params.value)}
              </Box>
            );
          },
        };
      }
      // --- CAC ---
      if (field === "cac") {
        return {
          ...baseColumn,
          renderCell: (params: any) => {
            const id = params.row.id;
            let style = cacHL.top.includes(id) ? redStyle : {};
            return (
              <Box
                sx={{
                  padding: "3px",
                  borderRadius: 1,
                  ...style,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  marginTop: "4px",
                  height: "26px",
                  width: "fit-content",
                }}
              >
                {params.value}
              </Box>
            );
          },
        };
      }
      // --- FTD ---
      if (field === "ftd") {
        return {
          ...baseColumn,
          sortComparator: ftdQftdSortComparator,
          renderCell: (params: any) => {
            const id = params.row.id;
            let style = ftdRed.includes(id) ? redStyle : {};
            return (
              <Box
                sx={{
                  padding: "3px",
                  borderRadius: 1,
                  ...style,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  marginTop: "4px",
                  height: "26px",
                  width: "fit-content",
                }}
              >
                {params.value}
              </Box>
            );
          },
        };
      }
      // --- QFTD ---
      if (field === "qftd") {
        return {
          ...baseColumn,
          sortComparator: ftdQftdSortComparator,
          renderCell: (params: any) => {
            const id = params.row.id;
            let style = qftdRed.includes(id) ? redStyle : {};
            return (
              <Box
                sx={{
                  padding: "3px",
                  borderRadius: 1,
                  ...style,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  marginTop: "4px",
                  height: "26px",
                  width: "fit-content",
                }}
              >
                {params.value}
              </Box>
            );
          },
        };
      }
      // --- IMP Share ---
      if (field === "imp_share") {
        return {
          ...baseColumn,
          renderCell: (params: any) => {
            const id = params.row.id;
            let style = {};
            if (impShareRed.includes(id)) style = redStyle;
            else if (impShareGreen.includes(id)) style = greenStyle;
            return (
              <Box
                sx={{
                  padding: "3px",
                  borderRadius: 1,
                  ...style,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  marginTop: "4px",
                  height: "26px",
                  width: "fit-content",
                }}
              >
                {formatPercentage(params.value)}
              </Box>
            );
          },
        };
      }
      // --- IMP Share Top ---
      if (field === "imp_share_top") {
        return {
          ...baseColumn,
          renderCell: (params: any) => {
            const id = params.row.id;
            let style = {};
            if (impShareTopRed.includes(id)) style = redStyle;
            else if (impShareTopGreen.includes(id)) style = greenStyle;
            return (
              <Box
                sx={{
                  padding: "3px",
                  borderRadius: 1,
                  ...style,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  marginTop: "4px",
                  height: "26px",
                  width: "fit-content",
                }}
              >
                {formatPercentage(params.value)}
              </Box>
            );
          },
        };
      }
      // --- IMP Share Abs Top ---
      if (field === "imp_share_abs_top") {
        return {
          ...baseColumn,
          renderCell: (params: any) => {
            const id = params.row.id;
            let style = {};
            if (impShareAbsTopRed.includes(id)) style = redStyle;
            else if (impShareAbsTopGreen.includes(id)) style = greenStyle;
            return (
              <Box
                sx={{
                  padding: "3px",
                  borderRadius: 1,
                  ...style,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  marginTop: "4px",
                  height: "26px",
                  width: "fit-content",
                }}
              >
                {formatPercentage(params.value)}
              </Box>
            );
          },
        };
      }
      // --- Average QS ---
      if (field === "avg_qs") {
        return {
          ...baseColumn,
          renderCell: (params: any) => {
            const id = params.row.id;
            let style = avgQSRed.includes(id) ? redStyle : {};
            return (
              <Box
                sx={{
                  padding: "3px",
                  borderRadius: 1,
                  ...style,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  marginTop: "4px",
                  height: "26px",
                  width: "fit-content",
                }}
              >
                {params.value}
              </Box>
            );
          },
        };
      }
      // --- CPC ---
      if (field === "cpc") {
        return {
          ...baseColumn,
          renderCell: (params: any) => {
            const id = params.row.id;
            let style = {};
            if (cpcHL.top.includes(id)) style = redStyle;
            else if (cpcHL.last.includes(id)) style = greenStyle;
            return (
              <Box
                sx={{
                  padding: "3px",
                  borderRadius: 1,
                  ...style,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  marginTop: "4px",
                  height: "26px",
                  width: "fit-content",
                }}
              >
                {formatCurrency(params.value)}
              </Box>
            );
          },
        };
      }
      // --- CVR ---
      if (field === "cvr") {
        return {
          ...baseColumn,
          renderCell: (params: any) => {
            const id = params.row.id;
            let style = {};
            if (cvrHL.top.includes(id)) style = redStyle;
            else if (cvrHL.last.includes(id)) style = greenStyle;
            return (
              <Box
                sx={{
                  padding: "3px",
                  borderRadius: 1,
                  ...style,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  marginTop: "4px",
                  height: "26px",
                  width: "fit-content",
                }}
              >
                {typeof params.value === "number"
                  ? `${(params.value * 100).toFixed(2)}%`
                  : params.value}
              </Box>
            );
          },
        };
      }
      // --- Profit ---
      if (field === "profit") {
        return {
          ...baseColumn,
          sortComparator: (_v1: any, _v2: any, param1: any, param2: any) => {
            const item1 = performanceData.find(
              (item: any) => item.kw_id === param1.id
            );
            const item2 = performanceData.find(
              (item: any) => item.kw_id === param2.id
            );
            const profit1 = item1 ? item1.Profit : 0;
            const profit2 = item2 ? item2.Profit : 0;
            return profit1 - profit2;
          },
          renderCell: (params: any) => {
            const id = params.row.id;
            let style = {};
            if (profitHL.red.includes(id)) style = redStyle;
            else if (profitHL.green.includes(id)) style = greenStyle;
            return (
              <Box
                sx={{
                  padding: "3px",
                  borderRadius: 1,
                  ...style,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  marginTop: "4px",
                  height: "26px",
                  width: "fit-content",
                }}
              >
                {params.value}
              </Box>
            );
          },
        };
      }
      // --- PV ---
      if (field === "pv") {
        return {
          ...baseColumn,
          sortComparator: (_v1: any, _v2: any, param1: any, param2: any) => {
            const item1 = performanceData.find(
              (item: any) => item.kw_id === param1.id
            );
            const item2 = performanceData.find(
              (item: any) => item.kw_id === param2.id
            );
            const pv1 = item1 ? item1.PV : 0;
            const pv2 = item2 ? item2.PV : 0;
            return pv1 - pv2;
          },
          renderCell: (params: any) => {
            const id = params.row.id;
            let style = {};
            if (pvHL.red.includes(id)) style = redStyle;
            else if (pvHL.green.includes(id)) style = greenStyle;
            return (
              <Box
                sx={{
                  padding: "3px",
                  borderRadius: 1,
                  ...style,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  marginTop: "4px",
                  height: "26px",
                  width: "fit-content",
                }}
              >
                {params.value}
              </Box>
            );
          },
        };
      }
      // --- NRPF ---
      if (field === "nrpf") {
        return {
          ...baseColumn,
          sortComparator: (_v1: any, _v2: any, param1: any, param2: any) => {
            const item1 = performanceData.find(
              (item: any) => item.kw_id === param1.id
            );
            const item2 = performanceData.find(
              (item: any) => item.kw_id === param2.id
            );
            const nrpf1 = item1 ? item1.NRPF : 0;
            const nrpf2 = item2 ? item2.NRPF : 0;
            return nrpf1 - nrpf2;
          },
          renderCell: (params: any) => {
            const id = params.row.id;
            let style = {};
            if (nrpfHL.red.includes(id)) style = redStyle;
            else if (nrpfHL.green.includes(id)) style = greenStyle;
            return (
              <Box
                sx={{
                  padding: "3px",
                  borderRadius: 1,
                  ...style,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  marginTop: "4px",
                  height: "26px",
                  width: "fit-content",
                }}
              >
                {params.value}
              </Box>
            );
          },
        };
      }
      // --- Clickin ---
      if (field === "clickin") {
        return {
          ...baseColumn,
          renderCell: (params: any) => (
            <Box
              sx={{
                padding: "3px",
                borderRadius: 1,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                marginTop: "4px",
                height: "26px",
                width: "fit-content",
              }}
            >
              {params.value}
            </Box>
          ),
        };
      }
      // --- Clickouts ---
      if (field === "clickouts") {
        return {
          ...baseColumn,
          renderCell: (params: any) => (
            <Box
              sx={{
                padding: "3px",
                borderRadius: 1,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                marginTop: "4px",
                height: "26px",
                width: "fit-content",
              }}
            >
              {params.value}
            </Box>
          ),
        };
      }
      // --- Keyword ---
      if (field === "keyword") {
        return {
          ...baseColumn,
          width: 200,
          renderCell: (params: any) => (
            <Box
              sx={{
                padding: "3px",
                borderRadius: 1,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                marginTop: "4px",
                height: "26px",
                width: "fit-content",
              }}
            >
              {params.value}
            </Box>
          ),
        };
      }
      // --- Icon fields ---
      const iconFields = [
        "Cpc_up",
        "cvr_down",
        "imp_share_down",
        "qs_down",
        "exact_traffic_mix",
      ];
      if (iconFields.includes(field)) {
        return {
          ...baseColumn,
          width: 80,
          renderCell: (params: any) => {
            const matchingItem = performanceData.find(
              (item: any) => item.kw_id === params.row.id
            );
            let showFlag = false;
            if (matchingItem) {
              switch (field) {
                case "Cpc_up":
                  showFlag = (matchingItem.cpc_diff_30_60 ?? 0) >= 0.3;
                  break;
                case "cvr_down":
                  showFlag = (matchingItem.cvr_diff_30_60 ?? 0) <= -0.1;
                  break;
                case "imp_share_down":
                  showFlag = (matchingItem.imp_diff_30_60 ?? 0) <= -0.3;
                  break;
                case "qs_down":
                  showFlag = (matchingItem.qs_diff_30_60 ?? 0) >= 0.3;
                  break;
                default:
                  showFlag = false;
              }
            }
            return showFlag ? (
              <Stack
                direction="row"
                justifyContent="left"
                alignItems="left"
                width="100%"
                mt={0.5}
              >
                <FlagCircleOutlinedIcon
                  sx={{ color: "rgba(211, 47, 47, 1)" }}
                />
              </Stack>
            ) : null;
          },
        };
      }
      return baseColumn;
    }
  );

  const formatDate = (date: string) => {
    if (!date) return "";
    const d = dayjs(date);
    return d.isValid() ? d.format("DD/MM/YYYY") : "";
  };

  return (
    <Paper sx={{ marginTop: "16px", paddingBottom: "16px" }}>
      <Stack
        direction={"row"}
        justifyContent={"space-between"}
        sx={{ padding: "16px 16px 8px 16px" }}
        alignItems={"center"}
      >
        <Stack direction={"column"}>
          <Typography sx={{ color: "#000000", fontWeight: "500" }}>
            Keywords performance
          </Typography>
          <Typography sx={{ color: "#000000", fontWeight: "500" }}>
            {dateRange?.startDate && dateRange?.endDate
              ? `${formatDate(dateRange.startDate)} - ${formatDate(
                  dateRange.endDate
                )}`
              : "Date range not available"}
          </Typography>
        </Stack>
        <Stack direction={"row"} gap={2}>
          <Autocomplete
            size="small"
            disablePortal
            options={timeFrames.filter(
              (tf: any) => tf.value !== selectedTimeframe
            )}
            value={
              timeFrames.find((tf) => tf.value === selectedTimeframe) ||
              timeFrames[0]
            }
            onChange={handleTimeframeChange}
            data-tracker={insightType === "adgroup" ? "305" : "326"}
            sx={{ width: 272 }}
            renderInput={(params) => (
              <TextField
                color="secondary"
                {...params}
                label="Timeframe"
                slotProps={{
                  inputLabel: {
                    shrink: true,
                  },
                }}
              />
            )}
          />
          <Autocomplete
            size="small"
            disablePortal
            disableClearable
            options={Keywords.filter(
              (option) => option.label !== selectedKeyword
            )}
            data-tracker={insightType === "adgroup" ? "306" : "327"}
            value={{ label: selectedKeyword }}
            onChange={(_, newValue: any) => {
              setSelectedKeyword(newValue?.label || "All");
            }}
            sx={{ width: 272 }}
            renderInput={(params) => (
              <TextField
                color="secondary"
                {...params}
                label="Keyword"
                slotProps={{
                  inputLabel: {
                    shrink: true,
                  },
                }}
                InputProps={{
                  ...params.InputProps,
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon sx={{ marginLeft: "12px" }} />
                    </InputAdornment>
                  ),
                }}
              />
            )}
          />
        </Stack>
      </Stack>
      <Box>
        <div style={{ height: 250, width: "100%"}}>
          <DataGridPro
            sx={{
              "& .MuiDataGrid-cell:focus, & .MuiDataGrid-cell:focus-within": {
                outline: "none",
              },
              "& .MuiDataGrid-columnHeader:focus, & .MuiDataGrid-columnHeader:focus-within":
                { outline: "none" },
              "& .MuiDataGrid-iconSeparator": { display: "none" },
              "& .MuiDataGrid-cellEmpty": {
                width: "auto",
                overflow: "hidden",
                textOverflow: "ellipsis",
              },
              "& .MuiDataGrid-row.Mui-selected": {
                backgroundColor: "transparent !important",
              },
              "& .MuiDataGrid-row.Mui-selected .MuiDataGrid-cell--pinnedLeft": {
                backgroundColor: "transparent !important",
              },
              "& .MuiDataGrid-row.Mui-selected .MuiDataGrid-cell--pinnedRight":
                { backgroundColor: "transparent !important" },

              "& .MuiDataGrid-cell--withRightBorder, & .MuiDataGrid-columnHeader--withRightBorder":
                { boxShadow: "2px 0 4px rgba(0, 0, 0, 0.1)" },
              "& .MuiDataGrid-cell--withLeftBorder, & .MuiDataGrid-columnHeader--withLeftBorder":
                { boxShadow: "-2px 0 4px rgba(0, 0, 0, 0.1)" },
              border: "none",
            }}
            density="compact"
            pagination={false}
            rows={processedRows}
            columns={columns}
            disableColumnMenu
            disableColumnFilter
            disableColumnReorder
            sortingOrder={["asc", "desc"]}
            hideFooter
            isCellEditable={() => false}
            disableRowSelectionOnClick={true}
            slots={{
              noRowsOverlay: NoRowsOverlay,
            }}
            initialState={{
              sorting: { sortModel: [{ field: "spend", sort: "desc" }] },
              pinnedColumns: {
                left: ["keyword", "match_type"],
                right: [
                  "Cpc_up",
                  "cvr_down",
                  "imp_share_down",
                  "qs_down",
                  "exact_traffic_mix",
                ],
              },
            }}
          />
        </div>
      </Box>
    </Paper>
  );
};

export default InsightKeyWordPerformance;
